import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { getAdminToBuyerQuotations, requestRevisionAdminToBuyerQuotation } from "@/lib/api/quotation.api"
import { useAuthStore } from "@/store/auth.store"
import { QuotationFilters } from "@/components/dashboard/shared/QuotationFilters"
import { useGetAdminToBuyerQuotationById } from "@/hooks/admin/useGetAdminToBuyerQuotationById"
import { usePlaceOrder } from "@/hooks/buyer/usePlaceOrder"
import { toast } from "sonner"
import { useBuyerUIStore } from "@/store/buyer-ui.store"
import { QuotationCard } from "@/components/dashboard/shared/QuotationCard"
import { TargetItemView } from "@/components/dashboard/shared/TargetItemView"
import React, { useState, useEffect } from "react"
import { useSearch, useNavigate } from "@tanstack/react-router"
import { cn } from "@/lib/utils"
import { CompanyDetailSidebar, ProductDetailSidebar, VariantDetailSidebar, ExternalSellerDetailSidebar, RfqDetailSidebar, QuotationDetailSidebar } from "@/components/dashboard/shared/SourcingDetailSidebars"
import { PromptModalCard } from "@/components/shared/PromptModalCard"
import {
  Receipt,
  Loader2,
  AlertCircle,
  FileText,
  Package,
  Search,
  SlidersHorizontal,
  ChevronRight,
  ChevronLeft,
  Check,
  FileCheck2,
  ShoppingCart,
  Building2,
  Sparkles,
  Zap,
  Plane,
  Pencil,
  Tag,
  MapPin,
  Hash,
  Ship,
  ExternalLink,
  ArrowRight,
  Layers,
  X,
  MoreVertical,
  RotateCcw,
  Calendar,
} from "lucide-react"

// Helper to format date
function formatDate(isoStr: string) {
  if (!isoStr) return "N/A"
  return new Date(isoStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}




// ── Quotation Detail View Component ──────────────────────────────────────────
export interface QuotationDetailsPageProps {
  quotationId?: string
  quotation?: any
  showBack?: boolean
  quotationIndex?: number
  totalQuotations?: number
  /** All quotations for this RFQ — used to build per-supplier history timeline */
  allQuotations?: any[]
}

export function QuotationDetailsPage({
  quotationId = "",
  quotation: passedQuotation,
  showBack = true,
  quotationIndex,
  totalQuotations,
  allQuotations = [],
}: QuotationDetailsPageProps) {
  const {
    data: detailData,
    isLoading: isDetailLoading,
    isError: isDetailError,
    error: detailError,
  } = useGetAdminToBuyerQuotationById(quotationId, !passedQuotation && !!quotationId)

  const quotation = passedQuotation || detailData?.adminToBuyerQuotation || null

  const validUntil =
    quotation?.expiresAt ||
    (detailData as any)?.buyerToAdminRfq?.quotations?.[0]?.expiresAt ||
    (quotation as any)?.buyerToAdminRfq?.quotations?.[0]?.expiresAt

  const isExpired = validUntil ? new Date(validUntil).getTime() < Date.now() : false
  const { mutate: placeOrderMutation, isPending: isPlacingOrder } = usePlaceOrder()
  const [orderSuccess, setOrderSuccess] = useState(false)
  const [orderError, setOrderError] = useState<string | null>(null)
  const [proceedWithoutShipment, setProceedWithoutShipment] = useState(false)
  const [shipmentSelectionEnabled, setShipmentSelectionEnabled] = useState(false)
  const navigate = useNavigate()

  const accessToken = useAuthStore((s) => s.accessToken)
  const queryClient = useQueryClient()

  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false)
  const actionMenuRef = React.useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target as Node)) {
        setIsActionMenuOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const rfqId = quotation?.rfqId || quotation?.buyerToAdminRfq?.id || quotation?.adminToSellerRfqId

  useEffect(() => {
    if (!passedQuotation && rfqId) {
      navigate({ to: "/dashboard/quotations/$rfqId", params: { rfqId } })
    }
  }, [passedQuotation, rfqId, navigate])

  const targetQuotationId = passedQuotation?.id || quotationId

  const { mutate: requestRevisionMutation, isPending: isRequestingRevision } = useMutation({
    mutationFn: () => requestRevisionAdminToBuyerQuotation(targetQuotationId, accessToken || ""),
    onSuccess: () => {
      if (passedQuotation?.rfqId) {
        queryClient.invalidateQueries({ queryKey: ["buyer-rfq-with-quotations", passedQuotation.rfqId] })
      } else {
        queryClient.invalidateQueries({ queryKey: ["admin-buyer-quotation-detail", targetQuotationId] })
      }
      toast.success("Revision Requested Successfully")
    },
    onError: () => {
      // Error toast is automatically handled by http.ts response interceptor
    }
  })

  // ── All useState hooks must be declared before any early returns ──
  const [selectedVariant, setSelectedVariant] = useState<any | null>(null)
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null)
  const [selectedCompany, setSelectedCompany] = useState<any | null>(null)
  const [selectedExternalSeller, setSelectedExternalSeller] = useState<any | null>(null)
  const [selectedProduct, setSelectedProduct] = useState<any | null>(null)
  const [selectedZipaRateId, setSelectedZipaRateId] = useState<string | null>(null)
  const [selectedRfq, setSelectedRfq] = useState<any>(null)
  const [showRevisionModal, setShowRevisionModal] = useState(false)
  const [showPlaceOrderModal, setShowPlaceOrderModal] = useState(false)
  const [selectedHistoryQuotation, setSelectedHistoryQuotation] = useState<any | null>(null)
  const [isCardHighlighted, setIsCardHighlighted] = useState(false)
  const cardRef = React.useRef<HTMLDivElement>(null)
  const highlightTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleHighlightCard = () => {
    setIsCardHighlighted(true)
    cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current)
    }
    highlightTimeoutRef.current = setTimeout(() => {
      setIsCardHighlighted(false)
    }, 2500)
  }

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current)
      }
    }
  }, [])

  // Quotations for the same supplier — used by the history timeline sidebar
  const companyQuotationHistory = allQuotations.length > 0 && passedQuotation?.companyId
    ? [...allQuotations]
        .filter((q: any) => q.companyId === passedQuotation.companyId)
        .sort((a: any, b: any) => (b.version ?? 0) - (a.version ?? 0))
    : []

  const onPlaceOrder = (zipaWorldQuotationId?: string) => {
    if (isExpired) {
      toast.error("Quotation expired: please revise it")
      return
    }
    setOrderError(null)
    placeOrderMutation({ quotationId: targetQuotationId, zipaWorldQuotationId }, {
      onSuccess: () => {
        toast.success("Order Placed Successfully")
        navigate({ to: "/dashboard/orders" as any })
      },
      onError: (err: any) => {
        setOrderError(err?.message || 'Failed to place order. Please try again.')
      },
    })
  }

  const handlePlaceOrderClick = () => {
    if (isExpired) {
      toast.error("Quotation expired: please revise it")
      return
    }
    const hasZipaQuotations = quotation?.zipaQuotations && quotation.zipaQuotations.length > 0
    // If quotation has rates and user hasn't yet enabled selection or selected a rate → always show toast first
    if (hasZipaQuotations && !selectedZipaRateId && !proceedWithoutShipment) {
      setShipmentSelectionEnabled(false)
      toast.custom((t) => (
        <div className="bg-white border border-slate-200 p-4 rounded-xl shadow-lg font-sans max-w-sm">
          <h4 className="text-sm font-bold text-slate-800">Shipment Option</h4>
          <p className="text-xs text-slate-500 mt-1 leading-normal">
            Do you want to add shipment before order proceed?
          </p>
          <div className="mt-3.5 flex gap-2 justify-end">
            <button 
              onClick={() => {
                toast.dismiss(t)
                setShipmentSelectionEnabled(true)
                const logisticsSectionId = `logistics-rates-section-${targetQuotationId}`
                const element = document.getElementById(logisticsSectionId)
                if (element) {
                  element.scrollIntoView({ behavior: "smooth" })
                }
              }}
              className="px-3 py-1.5 text-[11px] font-bold text-slate-650 bg-slate-100 hover:bg-slate-200 rounded-lg cursor-pointer transition-colors"
            >
              Add Shipment
            </button>
            <button 
              onClick={() => {
                toast.dismiss(t)
                setShowPlaceOrderModal(true)
              }}
              className="px-3 py-1.5 text-[11px] font-bold text-primary-foreground bg-primary hover:bg-primary/90 rounded-lg cursor-pointer transition-colors"
            >
              Proceed without Shipment
            </button>
          </div>
        </div>
      ), { duration: 8000 })
    } else {
      setShowPlaceOrderModal(true)
    }
  }

  const onBack = () => {
    navigate({ to: "/dashboard/quotations" as any })
  }

  const isCurrentlyLoading = isDetailLoading && !passedQuotation
  const isCurrentlyError = isDetailError && !passedQuotation

  if (isCurrentlyLoading) {
    return (
      <div className="relative flex flex-col items-center justify-center py-20 text-slate-400 gap-3 bg-white border border-primary/20 rounded-2xl shadow-sm w-full min-h-[360px] font-sans text-left">
        {showBack && (
          <div className="absolute top-5 left-5">
            <button
              onClick={onBack}
              className="flex items-center justify-center p-2 rounded-lg border border-primary/20 bg-white hover:bg-primary/5 hover:text-primary hover:shadow-sm transition-all cursor-pointer text-slate-800"
            >
              <ChevronLeft className="size-4" />
            </button>
          </div>
        )}
        <Loader2 className="size-8 animate-spin text-primary" />
        <p className="text-sm font-medium text-slate-500">Fetching quotation details...</p>
      </div>
    )
  }

  if (isCurrentlyError) {
    return (
      <div className="w-full space-y-4 font-sans text-left">
        {showBack && (
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="flex items-center justify-center p-2 rounded-lg border border-primary/20 bg-white hover:bg-primary/5 hover:text-primary hover:shadow-sm transition-all cursor-pointer text-slate-800">
              <ChevronLeft className="size-4" />
            </button>
          </div>
        )}
        <div className="flex items-center gap-3 rounded-2xl border border-destructive/20 bg-destructive/10 p-5 text-sm text-destructive">
          <AlertCircle className="size-5 shrink-0" />
          <p className="font-semibold">{(detailError as Error)?.message || 'Failed to load quotation details.'}</p>
        </div>
      </div>
    )
  }

  if (!quotation) {
    return (
      <div className="w-full space-y-4 font-sans text-left">
        {showBack && (
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="flex items-center justify-center p-2 rounded-lg border border-primary/20 bg-white hover:bg-primary/5 hover:text-primary hover:shadow-sm transition-all cursor-pointer text-slate-800">
              <ChevronLeft className="size-4" />
            </button>
          </div>
        )}
        <div className="flex items-center gap-3 rounded-2xl border border-yellow-100 bg-yellow-50 p-5 text-sm text-yellow-700">
          <AlertCircle className="size-5 shrink-0" />
          <p className="font-semibold">Quotation details not found.</p>
        </div>
      </div>
    )
  }

  const charges = quotation?.sellerGeneratedQuotationCharges || quotation?.adminToBuyerQuotationCharges || quotation?.charges || []
  const normStatus = (quotation?.status || "").toUpperCase()
  const isAccepted = normStatus === "ACCEPTED" || normStatus === "APPROVED"
  const isSuperseded = normStatus === "SUPERSEDED"
  const isPendingOrRevision =
    normStatus === "PENDING" ||
    normStatus === "REQUEST_REVISION" ||
    normStatus === "AWAITING_REVISED_QUOTATION" ||
    normStatus === "AWAITING_REVISE_QUOTATION"

  return (
    <div className="animate-in fade-in duration-200 text-left font-sans w-full space-y-4">
      {/* Order Error */}
      {orderError && (
        <div className="flex items-center gap-3 rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <AlertCircle className="size-4 shrink-0" />
          <p className="font-semibold text-xs">{orderError}</p>
        </div>
      )}
      <div 
        ref={cardRef}
        className={cn(
          "group relative overflow-hidden bg-white rounded-2xl p-5 space-y-4 shadow-sm hover:shadow-xs transition-all duration-300",
          isCardHighlighted
            ? "border-2 border-primary"
            : "border border-primary/20 hover:border-primary/30"
        )}
      >
        {/* Top Accent Glow */}
        <div className="absolute top-0 left-0 right-0 h-[1px] group-hover:h-[3.5px] bg-gradient-to-r from-primary to-primary/80 rounded-t-2xl opacity-0 group-hover:opacity-100 transition-all duration-300" />
        
        {/* Header Row */}
        <div className="flex flex-wrap items-center justify-between gap-y-3 gap-x-2 sm:gap-x-4">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            {showBack ? (
              <>
                <button
                  onClick={onBack}
                  className="flex items-center gap-2 rounded-lg border border-primary/20 bg-white hover:bg-primary/5 hover:text-primary hover:shadow-sm transition-all cursor-pointer p-1 shrink-0"
                >
                  <ChevronLeft className="size-4" />
                </button>
                <span className="text-sm text-slate-700 font-medium truncate">Back to Quotations</span>
              </>
            ) : (
              <div className="min-w-0">
                <h3 className="text-md font-bold text-slate-800 flex items-center gap-2 truncate">
                  <Receipt className="size-5 text-primary shrink-0" />
                  <span className="truncate">
                    {totalQuotations && totalQuotations > 1
                      ? `Quotation ${(quotationIndex ?? 0) + 1}`
                      : "Quotation"}
                  </span>
                </h3>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            {/* Status (desktop & mobile) */}
            <span className={cn(
              "inline-flex items-center gap-1 rounded px-2.5 py-1 text-[10px] font-bold border uppercase tracking-wider shrink-0 whitespace-nowrap",
              isAccepted
                ? "bg-[#e0f2fe] text-[#0369a1] border-sky-200"
                : isPendingOrRevision
                ? "bg-amber-50 text-amber-700 border-amber-100"
                : isSuperseded
                ? "bg-slate-100 text-slate-600 border-slate-200"
                : "bg-red-50 text-red-700 border-red-100"
            )}>
              {normStatus === "AWAITING_REVISED_QUOTATION" || normStatus === "AWAITING_REVISE_QUOTATION"
                ? "Awaiting Revision"
                : normStatus === "REQUEST_REVISION"
                ? "REQUEST REVISION"
                : quotation.status}
            </span>

            {/* Desktop Place Order Button */}
            {!isAccepted && !isSuperseded && (
              <button
                onClick={handlePlaceOrderClick}
                disabled={isPlacingOrder || orderSuccess}
                aria-disabled={isExpired}
                className={cn(
                  "hidden sm:inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-primary-foreground text-xs font-bold transition-all shadow-sm",
                  isExpired
                    ? "opacity-50 cursor-not-allowed bg-primary hover:bg-primary"
                    : "bg-primary hover:bg-primary/90 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                )}
              >
                {isPlacingOrder ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <ShoppingCart className="size-3.5" />
                )}
                {isPlacingOrder ? "Placing…" : "Place Order"}
              </button>
            )}

            {/* Vertical Ellipse Action Menu */}
            <div ref={actionMenuRef} className="relative shrink-0">
              <button
                type="button"
                onClick={() => setIsActionMenuOpen((prev) => !prev)}
                className="p-1.5 sm:p-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-500 hover:text-slate-700 cursor-pointer transition-colors"
              >
                <MoreVertical className="size-4" />
              </button>

              {isActionMenuOpen && (
                <div className="absolute right-0 top-9 z-10 w-40 rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
                  <button
                    type="button"
                    onClick={() => {
                      setIsActionMenuOpen(false)
                      setShowRevisionModal(true)
                    }}
                    disabled={isRequestingRevision || isAccepted || normStatus === "REQUEST_REVISION" || normStatus === "AWAITING_REVISED_QUOTATION" || normStatus === "AWAITING_REVISE_QUOTATION" || isSuperseded}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left text-[11px] font-bold tracking-wider text-slate-700 hover:bg-slate-50 hover:text-primary transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-slate-700"
                  >
                    <Pencil className="size-3.5" />
                    Request Revision
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Place Order Button in Second Row */}
          {!isAccepted && !isSuperseded && (
            <div className="w-full sm:hidden">
              <button
                onClick={handlePlaceOrderClick}
                disabled={isPlacingOrder || orderSuccess}
                aria-disabled={isExpired}
                className={cn(
                  "w-full flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-primary-foreground text-xs font-bold transition-all shadow-sm",
                  isExpired
                    ? "opacity-50 cursor-not-allowed bg-primary hover:bg-primary"
                    : "bg-primary hover:bg-primary/90 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                )}
              >
                {isPlacingOrder ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <ShoppingCart className="size-3.5" />
                )}
                {isPlacingOrder ? "Placing…" : "Place Order"}
              </button>
            </div>
          )}
        </div>

        <div className="h-px bg-primary/10 w-full" />

        {/* Grid Layout inside the card */}
        <div className={cn(
          "grid grid-cols-1 gap-6",
          companyQuotationHistory.length > 1 && "lg:grid-cols-4"
        )}>
          {/* Main Column */}
          <div className={cn(
            "space-y-4",
            companyQuotationHistory.length > 1 && "lg:col-span-3"
          )}>
           
            {/* Sourcing Target */}
            {(() => {
              const rfqData = quotation.rfq || quotation.buyerToAdminRfq || quotation.adminToSellerRfq || null
              const targets = (rfqData?.targets || []).filter(
                (t: any) => t.product || t.productVariant || t.productId || t.productVariantId
              )

              if (targets.length === 0) return null

              return (
                <div className="space-y-1.5">
                  <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                    <Sparkles className="size-3.5 text-primary" /> Sourcing Target
                  </h4>
                  <div className="grid grid-cols-1 gap-2">
                    {targets.map((target: any) => (
                      <TargetItemView 
                        key={target.id} 
                        target={target} 
                        onCompanyClick={setSelectedCompany}
                        onProductClick={setSelectedProduct}
                        onVariantClick={setSelectedVariant}
                        onExternalSellerClick={setSelectedExternalSeller}
                      />
                    ))}
                  </div>
                </div>
              )
            })()}
           
            {/* Quotation Company */}
            {quotation?.company?.legalName && (
              <div className="space-y-1.5">
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                  <Building2 className="size-3.5 text-primary" /> Quotation Company
                </h4>
                <div 
                  onClick={() => setSelectedCompany(quotation.company)}
                  className="group/item flex items-center justify-between gap-4 rounded-xl border border-primary/15 hover:border-primary/30 bg-slate-50/40 hover:bg-slate-50 p-3 shadow-sm transition-all duration-200 cursor-pointer"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm text-slate-700 font-medium hover:text-primary hover:underline text-xs tracking-tight font-sans">
                      {quotation.company.legalName}
                    </span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Quotation Message */}
            <div className="space-y-1.5">
              <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                <FileText className="size-3.5 text-primary" /> Quotation Message
              </h4>
              <p className="text-sm text-slate-700 font-medium bg-primary/[0.03] p-3.5 rounded-xl border border-primary/15 hover:border-primary/25 transition-colors leading-relaxed italic">
                {quotation.message || "No message provided"}
              </p>
            </div>

            {/* Valid Until */}
            {validUntil && (
              <div className="space-y-1.5">
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                  <Calendar className="size-3.5 text-primary" /> Valid Until
                </h4>
                <div className="flex items-center justify-between gap-3 rounded-xl border border-primary/15 hover:border-primary/25 bg-slate-50/40 p-3 shadow-sm transition-all duration-200">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={cn(
                      "flex items-center justify-center size-8 rounded-lg border shrink-0",
                      isExpired
                        ? "bg-red-50 text-red-600 border-red-200/60"
                        : "bg-amber-50 text-amber-600 border-amber-200/60"
                    )}>
                      <Calendar className="size-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-slate-800 leading-snug">
                        {formatDate(validUntil)}
                      </p>
                      <p className="text-[10px] text-slate-500 font-medium">
                        {isExpired
                          ? "This quotation has expired"
                          : "This quotation is valid until this date"}
                      </p>
                    </div>
                  </div>
                  {isExpired && (
                    <span className="shrink-0 text-[10px] font-bold text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full uppercase tracking-wider">
                      Expired
                    </span>
                  )}
                </div>
              </div>
            )}


            {/* Charges Breakdown */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1 font-sans">
                  <Package className="size-3.5 text-primary" /> Charges Breakdown
                </h4>
              </div>
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm mt-3">
                {charges.length === 0 ? (
                  <div className="p-8 text-center text-slate-400 italic text-xs">No charges specified</div>
                ) : (
                <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs whitespace-nowrap">
                  <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <tr>
                      <th className="p-3 min-w-[160px]">Description</th>
                      <th className="p-3 text-center w-32">Unit Price</th>
                      <th className="p-3 text-center w-24">Quantity</th>
                      <th className="p-3 text-center w-24">Unit</th>
                      <th className="p-3 text-center w-28">Discount (%)</th>
                      <th className="p-3 text-center w-24">Tax (%)</th>
                      <th className="p-3 text-center w-24">Tax Type</th>
                      <th className="p-3 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 bg-white">
                    {charges.map((charge: any) => {
                        const isAdvanced = charge.unitPrice !== undefined;
                        const desc = charge.description || "N/A"
                        const price = isAdvanced ? Number(charge.unitPrice || 0) : Number(charge.amount || 0)
                        const qty = isAdvanced ? ((charge.quantity !== null && charge.quantity !== undefined && charge.quantity !== "") ? Number(charge.quantity) : 1) : 1
                        const disc = isAdvanced ? Number(charge.discount || 0) : 0
                        const tax = isAdvanced ? Number(charge.tax || 0) : 0
                        
                        const base = price * qty
                        const sub = base - (base * disc / 100)
                        const lineTotal = sub + (sub * tax / 100)

                        return (
                          <tr key={charge.id || charge.description} className="hover:bg-slate-50/50 transition-colors">
                            <td className="p-3 text-slate-800 font-semibold">
                              {(charge.productVariantId || charge.productVariant) ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const vId = charge.productVariantId || charge.productVariant?.id
                                    if (vId) {
                                      setSelectedVariantId(vId)
                                    } else if (charge.productVariant) {
                                      setSelectedVariant(charge.productVariant)
                                    }
                                  }}
                                  className="text-primary hover:text-primary/80 hover:underline text-left cursor-pointer transition-colors"
                                >
                                  {desc}
                                </button>
                              ) : (
                                desc
                              )}
                            </td>
                            <td className="p-3 text-center text-slate-600">{isAdvanced ? charge.unitPrice : "-"}</td>
                            <td className="p-3 text-center text-slate-600">{isAdvanced && charge.quantity !== null && charge.quantity !== undefined && charge.quantity !== "" ? charge.quantity : "-"}</td>
                            <td className="p-3 text-center text-slate-600">{isAdvanced ? (charge.measureUnit || charge.unit || charge.quantityUnit || "-") : "-"}</td>
                            <td className="p-3 text-center text-slate-600">{isAdvanced ? (charge.discount || 0) : "-"}</td>
                            <td className="p-3 text-center text-slate-600">{isAdvanced ? (charge.tax || 0) : "-"}</td>
                            <td className="p-3 text-center text-slate-600">
                              {isAdvanced && charge.taxType ? <span className="text-[9px] font-bold bg-slate-100 text-slate-500 px-1 py-0.5 rounded uppercase border border-slate-200">{charge.taxType}</span> : "-"}
                            </td>
                            <td className="p-3 text-right text-slate-900 font-bold font-mono">
                              {lineTotal.toFixed(2)}
                            </td>
                          </tr>
                        )
                      })
                    }
                  </tbody>
                </table>
                </div>
                )}
              </div>

              {/* Grand Total */}
              {(() => {
                const chargesTotal = charges.reduce((acc: number, charge: any) => {
                  const isAdvanced = charge.unitPrice !== undefined;
                  const price = isAdvanced ? Number(charge.unitPrice || 0) : Number(charge.amount || 0)
                  const qty = isAdvanced ? ((charge.quantity !== null && charge.quantity !== undefined && charge.quantity !== "") ? Number(charge.quantity) : 1) : 1
                  const disc = isAdvanced ? Number(charge.discount || 0) : 0
                  const tax = isAdvanced ? Number(charge.tax || 0) : 0
                  
                  const base = price * qty
                  const sub = base - (base * disc / 100)
                  const lineTotal = sub + (sub * tax / 100)
                  return acc + lineTotal
                }, 0)
                const grandTotal = (quotation.totalAmount !== undefined && quotation.totalAmount !== null)
                  ? Number(quotation.totalAmount)
                  : chargesTotal

                return (
                  <div className="flex justify-end p-3 bg-white border border-slate-200 rounded-xl mt-3 shadow-sm">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mr-3 self-center">Grand Total:</span>
                    <span className="text-[12px] font-bold text-primary font-mono">
                      {quotation.currencyCode} {grandTotal.toFixed(2)}
                    </span>
                  </div>
                )
              })()}
            </div>

            {/* Estimated Logistics Rates (Zipaworld) */}
            {quotation.zipaQuotations && quotation.zipaQuotations.length > 0 && (
              <div id={`logistics-rates-section-${targetQuotationId}`} className="space-y-3 pt-2">
                <h4 className="text-[10.5px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                  <Zap className="size-3.5 text-amber-500 fill-amber-500/20" /> Select Preferred Logistics Rate (Optional)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {quotation.zipaQuotations.map((zq: any) => {
                    const isAir = zq.shipmentMode?.toUpperCase() === "AIR"
                    const rateId = zq.id || zq.rateId
                    const isSelected = selectedZipaRateId === rateId
                    return (
                      <div 
                        key={rateId} 
                        onClick={() => {
                          if (isAccepted || isSuperseded) return
                          if (!shipmentSelectionEnabled) return
                          setSelectedZipaRateId(prev => prev === rateId ? null : rateId)
                          setProceedWithoutShipment(false)
                        }}
                        className={cn(
                          "relative overflow-hidden group/zq flex flex-col justify-between rounded-xl border transition-all duration-300 p-4.5 space-y-4",
                          (!isAccepted && !isSuperseded && shipmentSelectionEnabled)
                            ? "cursor-pointer"
                            : "cursor-default",
                          isSelected
                            ? "border-primary bg-primary/5 ring-2 ring-primary/10 shadow-sm"
                            : "border-slate-200/80 bg-white hover:border-primary/30 hover:shadow-xs"
                        )}
                      >
                        {/* Shipment Mode Indicator & Service */}
                        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                          <div className="flex items-center gap-2">
                            <div className={`p-2 rounded-lg border ${
                              isSelected
                                ? "bg-primary/10 border-primary/20 text-primary"
                                : isAir 
                                ? "bg-sky-50 border-sky-100 text-sky-600" 
                                : "bg-blue-50 border-blue-100 text-blue-600"
                            }`}>
                              {isAir ? <Plane className="size-4" /> : <Ship className="size-4" />}
                            </div>
                            <div>
                              <h5 className="font-extrabold text-slate-800 text-[13px] leading-tight flex items-center gap-1.5">
                                {zq.serviceName || "Standard"}
                                {isSelected && (
                                  <span className="bg-primary text-primary-foreground rounded-full p-0.5 inline-flex items-center justify-center size-3.5">
                                    <Check className="size-2.5" />
                                  </span>
                                )}
                              </h5>
                              <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mt-0.5">
                                {zq.quotationType?.replace(/_/g, " ") || "LOGISTICS RATE"}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded border uppercase tracking-wider ${
                              isSelected 
                                ? "bg-primary/15 text-primary border-primary/20 font-extrabold" 
                                : "text-slate-500 bg-slate-100 border-slate-200/50"
                            }`}>
                              {zq.transitTime || "N/A"}
                            </span>
                          </div>
                        </div>

                        {/* Origin -> Destination Route */}
                        <div className="grid grid-cols-5 items-center gap-2 text-center py-1">
                          <div className="col-span-2 text-left min-w-0">
                            <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider block">ORIGIN</span>
                            <span className="font-bold text-slate-700 text-xs truncate block" title={zq.origin}>{zq.origin || "N/A"}</span>
                          </div>
                          <div className="col-span-1 flex flex-col items-center justify-center">
                            <ArrowRight className="size-3 text-slate-400 group-hover/zq:translate-x-0.5 transition-transform" />
                          </div>
                          <div className="col-span-2 text-right min-w-0">
                            <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider block">DESTINATION</span>
                            <span className="font-bold text-slate-700 text-xs truncate block" title={zq.destination}>{zq.destination || "N/A"}</span>
                          </div>
                        </div>

                        {/* Charges breakdown details */}
                        <div className="bg-slate-50/50 border border-slate-200/60 rounded-xl p-3.5 text-xs font-semibold text-slate-600 space-y-2">
                          <div className="flex justify-between items-center text-[11.5px]">
                            <span className="text-slate-400">Freight Amount:</span>
                            <span className="font-mono text-slate-700">{zq.freightAmount || "N/A"}</span>
                          </div>
                          <div className="flex justify-between items-center text-[11.5px]">
                            <span className="text-slate-400">GST:</span>
                            <span className="font-mono text-slate-700">{zq.gst || "N/A"}</span>
                          </div>
                          <div className="h-px bg-slate-200/60 my-1" />
                          <div className="flex justify-between items-center text-xs font-bold">
                            <span className="text-slate-800">Total Amount:</span>
                            <span className="text-primary font-mono font-black">{zq.totalAmount || "N/A"}</span>
                          </div>
                        </div>

                        {/* View Rates Link Button */}
                        {zq.viewRates && (
                          <div className="pt-1" onClick={(e) => e.stopPropagation()}>
                            <a 
                              href={zq.viewRates} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="w-full h-9 border border-primary/20 text-primary hover:bg-primary/5 hover:border-primary/40 rounded-lg flex items-center justify-center gap-1.5 text-xs font-bold transition-all cursor-pointer shadow-sm"
                            >
                              <ExternalLink className="size-3.5" /> View Rates Details
                            </a>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Right Column: Quote History inside quotation card */}
          {companyQuotationHistory.length > 1 && (
            <div className="lg:col-span-1 space-y-3 lg:border-l lg:border-primary/10 lg:pl-6 text-left border-t border-primary/10 pt-4 lg:border-t-0 lg:pt-0">
              <div className="flex items-center justify-between">
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                  <RotateCcw className="size-3.5 text-primary" /> Quote History
                </h4>
                <span className="text-[9px] font-bold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                  {companyQuotationHistory.length} versions
                </span>
              </div>

              <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1 scrollbar-thin">
                {companyQuotationHistory.map((q: any, idx: number) => {
                  const isLatestQ = idx === 0
                  const version = q.version ?? (companyQuotationHistory.length - idx)
                  const charges = q.charges || q.adminToBuyerQuotationCharges || []
                  const computedTotal = charges.reduce((sum: number, c: any) => {
                    const qty = (c.quantity !== null && c.quantity !== undefined && c.quantity !== "") ? Number(c.quantity) : 1
                    const base = (c.unitPrice ?? 0) * qty
                    const after = base - (base * (c.discount ?? 0) / 100)
                    return sum + after + (after * (c.tax ?? 0) / 100)
                  }, 0)

                  const getStatusStyle = (s: string) => {
                    s = (s || '').toUpperCase()
                    if (s === 'ACCEPTED' || s === 'APPROVED') return 'bg-sky-50 text-sky-700 border-sky-100'
                    if (s === 'PENDING' || s === 'QUOTED') return 'bg-amber-50 text-amber-700 border-amber-100'
                    if (s === 'SUPERSEDED') return 'bg-slate-100 text-slate-500 border-slate-200'
                    return 'bg-red-50 text-red-700 border-red-100'
                  }

                  return (
                    <div
                      key={q.id || idx}
                      onClick={() => {
                        if (isLatestQ) {
                          handleHighlightCard()
                        } else {
                          setSelectedHistoryQuotation(q)
                        }
                      }}
                      className={cn(
                        "relative rounded-xl border p-2.5 cursor-pointer transition-all duration-200 group/hq space-y-1.5",
                        isLatestQ
                          ? "bg-primary/[0.03] border-primary/25 hover:bg-primary/[0.07] hover:border-primary/40 shadow-2xs"
                          : "bg-white border-slate-200/80 hover:border-primary/30 hover:bg-slate-50/80 shadow-2xs"
                      )}
                    >
                      {/* Row 1: Version number + status */}
                      <div className="flex items-center justify-between gap-1.5">
                        <div className="flex items-center gap-1.5 min-w-0">
                          
                          <span className="text-[11px] font-bold text-slate-800 truncate">
                            Version {version}
                          </span>
                        </div>
                        <span className={cn(
                          "shrink-0 text-[8px] font-bold border rounded px-1.5 py-0.5 uppercase tracking-wide",
                          isLatestQ ? "bg-emerald-50 text-emerald-700 border-emerald-100" : getStatusStyle(q.status)
                        )}>
                          {isLatestQ ? "Latest" : q.status || "Previous"}
                        </span>
                      </div>

                      {/* Row 2: Total + computedTotal */}
                      <div className="flex items-center justify-between gap-1.5 text-slate-500 pt-2">
                        <span className="text-[10px] font-semibold text-slate-500">
                          Total
                        </span>
                        <span className="text-[11px] font-extrabold text-primary font-mono">
                          {q.currencyCode || quotation.currencyCode} {computedTotal > 0 ? computedTotal.toFixed(2) : (q.totalAmount ? Number(q.totalAmount).toFixed(2) : "0.00")}
                        </span>
                      </div>
                      
                      {/* Row 3: Date + View */}
                      <div className="flex items-center justify-between gap-1.5 text-[9.5px] text-slate-400 font-medium pt-1 border-t border-slate-100/80">
                        <span>
                          {q.createdAt ? new Date(q.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                        </span>
                        <span className="text-primary font-bold flex items-center gap-0.5 group-hover/hq:underline">
                          View <ChevronRight className="size-2.5" />
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </div>
      {(selectedVariant || selectedVariantId) && (
        <VariantDetailSidebar
          variant={selectedVariant || undefined}
          variantId={selectedVariantId || undefined}
          onClose={() => {
            setSelectedVariant(null)
            setSelectedVariantId(null)
          }}
          onCompanyClick={(c) => setSelectedCompany(c)}
        />
      )}
      {selectedProduct && (
        <ProductDetailSidebar
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onCompanyClick={(c) => setSelectedCompany(c)}
        />
      )}
      {selectedCompany && (
        <CompanyDetailSidebar 
        company={selectedCompany} 
        onClose={() => setSelectedCompany(null)} 
      />
      )}
      {selectedExternalSeller && (
        <ExternalSellerDetailSidebar 
        externalSeller={selectedExternalSeller} 
        onClose={() => setSelectedExternalSeller(null)} 
      />
      )}
      {selectedRfq && (
        <RfqDetailSidebar 
          rfq={{
            ...selectedRfq,
            company: selectedRfq.company || quotation?.company
          }}
          onClose={() => setSelectedRfq(null)} 
        />
      )}

      {/* Older version detail sidebar — triggered from the inline right-column history panel on lg+ */}
      {selectedHistoryQuotation && (
        <QuotationDetailSidebar
          quotation={selectedHistoryQuotation}
          onClose={() => setSelectedHistoryQuotation(null)}
          showLinkedRfq={false}
        />
      )}

      {showRevisionModal && (
        <PromptModalCard
          title="Request Quotation Revision?"
          description="Are you sure you want to request a revision for this quotation? The seller/admin will be notified to review and update the quote details."
          icon={Pencil}
          iconContainerClassName="bg-primary/10 text-primary"
          iconClassName="size-6"
          primaryAction={{
            label: "Cancel",
            className: "flex-1 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 text-[13px] font-bold py-2.5 transition active:scale-95 cursor-pointer",
            onClick: () => setShowRevisionModal(false)
          }}
          secondaryAction={{
            label: isRequestingRevision ? "Requesting..." : "Request revision",
            disabled: isRequestingRevision || normStatus === "REQUEST_REVISION" || isAccepted || isSuperseded,
            className: "flex-1 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground text-[13px] font-bold py-2.5 transition active:scale-95 cursor-pointer shadow-sm disabled:opacity-50 disabled:cursor-not-allowed",
            onClick: () => {
              requestRevisionMutation(undefined, {
                onSuccess: () => {
                  setShowRevisionModal(false)
                }
              })
            }
          }}
          onClose={() => setShowRevisionModal(false)}
          onCloseText=""
        />
      )}

      {showPlaceOrderModal && (
        <PromptModalCard
          title="Place Order Confirmation"
          description="Are you sure you want to place an order for this quotation? Proceeding will convert this quotation into an active order."
          icon={ShoppingCart}
          iconContainerClassName="bg-primary/10 text-primary"
          iconClassName="size-6"
          primaryAction={{
            label: "Cancel",
            className: "flex-1 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 text-[13px] font-bold py-2.5 transition active:scale-95 cursor-pointer",
            onClick: () => setShowPlaceOrderModal(false)
          }}
          secondaryAction={{
            label: isPlacingOrder ? "Placing..." : "Place order",
            disabled: isPlacingOrder,
            className: "flex-1 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground text-[13px] font-bold py-2.5 transition active:scale-95 cursor-pointer shadow-sm disabled:opacity-50",
            onClick: () => {
              setShowPlaceOrderModal(false)
              onPlaceOrder(selectedZipaRateId || undefined)
            }
          }}
          onClose={() => setShowPlaceOrderModal(false)}
          onCloseText=""
        />
      )}
    </div>
  );
}

